import React, { useEffect, useState, useMemo } from 'react';
import { Modal } from '@mui/material';
import { toast } from 'react-toastify';
import { getUserInfoDetails } from '../../../../utlis';
import { assignJobToCandidate } from '../../../../api/sales';
import axiosInstance from '../../../../api/axiosInstance';
import MyTable from '../../../../components/MyTable';

const AssignJobModal = ({ isOpen, onClose, jobData, onSaveSuccess, salesPersonData }) => {
  const { companyId, clientId } = useMemo(() => getUserInfoDetails(), []);

  const [candidates, setCandidates] = useState([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [selectedRowIndices, setSelectedRowIndices] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Resolve sales_person_id to name
  const lookupSalesPersonName = (id) => {
    if (!id || !salesPersonData) return 'N/A';
    const person = salesPersonData.find(
      (p) => p.manager_id === id || p.sales_person_id === id
    );
    return person ? person.first_name : 'N/A';
  };

  // Fetch candidates when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCandidates();
      setSelectedRowIndices([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  const fetchCandidates = async () => {
    setIsLoadingCandidates(true);
    try {
      const res = await axiosInstance.get('/candidates', {
        params: { status: 'Active', pageNum: 1, pageSize: 100, options: 'mktCheck=True' },
      });
      setCandidates(res.data.data.records || []);
    } catch (error) {
      toast.error('Failed to fetch candidates');
      setCandidates([]);
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  // Transform candidates for display
  const transformedCandidates = useMemo(() => {
    return candidates.map((c) => ({
      candidate_id: c.candidate_id,
      sales_person_id: c.sales_person_id,
      name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
      technology: c.division || 'N/A',
      salesPerson: lookupSalesPersonName(c.sales_person_id),
    }));
  }, [candidates, salesPersonData]);

  // Apply search filter
  const filteredCandidates = useMemo(() => {
    if (!searchTerm) return transformedCandidates;
    const searchLower = searchTerm.toLowerCase();
    return transformedCandidates.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(searchLower) ||
        (c.technology || '').toLowerCase().includes(searchLower) ||
        (c.salesPerson || '').toLowerCase().includes(searchLower)
    );
  }, [transformedCandidates, searchTerm]);

  const columns = [
    { name: 'candidate_id', label: 'ID', options: { display: false } },
    {
      name: 'name',
      label: 'Candidate Name',
      options: {
        customBodyRender: (value) => (
          <span className="font-semibold text-secondary-800">{value}</span>
        ),
      },
    },
    { name: 'technology', label: 'Technology' },
    { name: 'salesPerson', label: 'Sales Person' },
  ];

  const tableOptions = {
    filterType: 'dropdown',
    responsive: 'standard',
    selectableRows: 'multiple',
    selectableRowsOnClick: true,
    rowsSelected: selectedRowIndices,
    onRowSelectionChange: (currentRowsSelected, allRowsSelected, rowsSelected) => {
      setSelectedRowIndices(rowsSelected);
    },
    elevation: 0,
    filter: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 25],
    selectableRows: 'multiple',
    textLabels: {
      body: {
        noMatch: isLoadingCandidates ? '' : 'No active candidates found',
      },
    },
  };

  // Get selected candidates from the filtered list
  const getSelectedCandidates = () => {
    return selectedRowIndices
      .map((index) => filteredCandidates[index])
      .filter((c) => c?.candidate_id);
  };

  const handleSave = async () => {
    const selectedCandidates = getSelectedCandidates();
    if (selectedCandidates.length === 0) return;

    setIsSaving(true);
    let successCount = 0;
    let failCount = 0;
    let lastError = '';

    for (const candidate of selectedCandidates) {
      try {
        await assignJobToCandidate(jobData, candidate.candidate_id, jobData.job_id, candidate.sales_person_id);
        successCount++;
      } catch (error) {
        const errMsg = error?.response?.data?.details || error?.response?.data?.message || error.message;
        console.error('Assign job failed for candidate', candidate.candidate_id, errMsg);
        lastError = errMsg;
        failCount++;
      }
    }

    setIsSaving(false);

    if (failCount > 0) {
      toast.error(lastError || `Failed to assign job to ${failCount} candidate(s)`);
    }
    if (successCount > 0) {
      toast.success(`Job assigned to ${successCount} candidate(s) successfully`);
      onClose();
      setTimeout(() => onSaveSuccess(), 100);
    }
  };

  const selectedCount = selectedRowIndices.length;
  const canSave = selectedCount > 0 && !isSaving;

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full md:w-[900px] max-w-4xl max-h-[85vh] flex flex-col border border-gray-200 relative">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-secondary-800">Assign Job</h2>
              <p className="text-sm text-text-hint mt-1">{jobData?.job_title}</p>
            </div>
            <div className="flex items-center gap-3">
              {selectedCount > 0 && (
                <span className="text-sm text-secondary-700 font-medium">
                  {selectedCount} selected
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={!canSave}
                className={`py-2 px-6 rounded-lg shadow transition ${
                  canSave
                    ? 'bg-secondary-700 text-text-light hover:bg-secondary-800 cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-500 hover:text-secondary-800 text-2xl font-bold px-2"
                aria-label="Close"
              >
                &times;
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSelectedRowIndices([]);
              }}
              placeholder="Search by name, technology, sales person..."
              className="search-input border-radius-10 w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          {/* Table */}
          <div className="flex-1 overflow-y-auto relative">
            <MyTable
              title=""
              data={isLoadingCandidates ? [] : filteredCandidates}
              columns={columns}
              options={tableOptions}
            />
            {isLoadingCandidates && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center z-20"
                style={{ background: 'rgba(255,255,255,0.9)' }}
              >
                <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
                <span className="text-lg text-secondary-700 font-semibold">Loading candidates...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AssignJobModal;
